import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, User, Trash2, ChevronRight } from "lucide-react";
import { api } from "@/lib/handball";
import { GoalkeeperDialog } from "@/components/Dialogs";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ProfilePicker() {
  const [goalkeepers, setGoalkeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gkOpen, setGkOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [license, setLicense] = useState({ license_count: 1, used: 0, available: 1, pending_request: null });
  const [requestingLicense, setRequestingLicense] = useState(false);
  const [licenseChecked, setLicenseChecked] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [gks, lic] = await Promise.all([
        api.get("/goalkeepers"),
        api.get("/license/status"),
      ]);
      setGoalkeepers(gks.data);
      setLicense(lic.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNewProfile = () => {
    setLicenseChecked(false);
    if (license.available > 0) {
      setGkOpen(true);
    } else {
      setLicenseOpen(true);
    }
  };

  const submitLicenseRequest = async () => {
    if (!licenseChecked) {
      toast.error("Bocka i rutan för att skicka förfrågan");
      return;
    }
    setRequestingLicense(true);
    try {
      const { data } = await api.post("/license/request");
      if (data.already) {
        toast.info("Du har redan en väntande förfrågan");
      } else {
        toast.success("Förfrågan om ytterligare licens skickad");
      }
      setLicenseOpen(false);
      load();
    } catch {
      toast.error("Kunde inte skicka förfrågan");
    } finally {
      setRequestingLicense(false);
    }
  };

  const createGoalkeeper = async (name) => {
    const { data } = await api.post("/goalkeepers", { name });
    toast.success(`Målvakt ${data.name} skapad`);
    setGkOpen(false);
    navigate(`/gk/${data.id}`);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/goalkeepers/${deleteId}`);
      toast.success("Målvakt borttagen");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Kunde inte ta bort målvakt");
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Profiler" backTo="/" backLabel="Startsida" />

      <main className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
              Målvakter
            </div>
            <h1 className="font-display font-black uppercase tracking-widest text-4xl md:text-5xl text-white">
              Hantera profiler
            </h1>
            <div className="text-zinc-400 mt-2">
              Öppna en profil för att se stats, dagbok och delning – eller skapa en ny.
            </div>
          </div>
          <Button
            data-testid="new-goalkeeper-btn"
            onClick={openNewProfile}
            className="h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
          >
            <Plus size={18} className="mr-2" />
            Ny profil
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : goalkeepers.length === 0 ? (
          <div
            className="card-tactical p-16 text-center"
            data-testid="empty-goalkeepers"
          >
            <div className="font-display font-black text-2xl uppercase tracking-widest mb-3">
              Inga profiler ännu
            </div>
            <div className="text-zinc-400 mb-8">
              Skapa den första målvaktsprofilen för att komma igång.
            </div>
            <Button
              data-testid="empty-new-goalkeeper-btn"
              onClick={() => setGkOpen(true)}
              className="h-12 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
            >
              <Plus size={18} className="mr-2" />
              Ny målvakt
            </Button>
          </div>
        ) : (
          <>
            {goalkeepers.some((g) => g.role === "owner" || !g.role) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {goalkeepers.filter((g) => g.role === "owner" || !g.role).map((g) => (
                  <div
                    key={g.id}
                    className="card-tactical p-5 hover:border-[#34C759] transition-colors group relative"
                    data-testid={`goalkeeper-card-${g.id}`}
                  >
                    <Link to={`/gk/${g.id}`} className="block">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
                          <User size={18} className="text-[#34C759]" />
                        </div>
                        <div className="font-display font-black uppercase tracking-widest text-xl text-white leading-tight">
                          {g.name}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#27272A]">
                        <span className="text-[11px] text-zinc-500 uppercase tracking-widest font-display">
                          Öppna
                        </span>
                        <ChevronRight
                          size={18}
                          className="text-zinc-500 group-hover:text-[#34C759] transition-colors"
                        />
                      </div>
                    </Link>
                    <button
                      data-testid={`goalkeeper-delete-${g.id}`}
                      onClick={() => setDeleteId(g.id)}
                      className="absolute top-3 right-3 p-1.5 text-zinc-600 hover:text-[#FF3B30] transition-colors"
                      aria-label="Ta bort profil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {goalkeepers.some((g) => g.role === "editor" || g.role === "viewer") && (
              <section className="mt-10" data-testid="shared-with-me-section">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-4">
                  Delat med mig
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {goalkeepers.filter((g) => g.role === "editor" || g.role === "viewer").map((g) => (
                    <Link
                      key={g.id}
                      to={`/gk/${g.id}`}
                      data-testid={`shared-goalkeeper-card-${g.id}`}
                      className="card-tactical p-5 hover:border-[#FFD60A] transition-colors group block"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
                          <User size={18} className="text-[#FFD60A]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-black uppercase tracking-widest text-xl text-white leading-tight truncate">
                            {g.name}
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                            Delad av {g.owner_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#27272A]">
                        <span
                          className={`text-[9px] font-bold tracking-widest border px-1.5 py-0.5 ${
                            g.role === "editor"
                              ? "border-[#FFD60A] text-[#FFD60A]"
                              : "border-[#34C759] text-[#34C759]"
                          }`}
                        >
                          {g.role === "editor" ? "SKRIBENT" : "LÄSARE"}
                        </span>
                        <ChevronRight
                          size={18}
                          className="text-zinc-500 group-hover:text-[#FFD60A] transition-colors"
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <GoalkeeperDialog
        open={gkOpen}
        onOpenChange={setGkOpen}
        onCreate={createGoalkeeper}
      />

      <Dialog open={licenseOpen} onOpenChange={setLicenseOpen}>
        <DialogContent
          data-testid="license-dialog"
          className="bg-[#111] border-[#27272A] text-white rounded-none"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-xl">
              Ytterligare licens
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Din fria licens tillåter en profil. För att skapa fler
              profiler krävs godkännande av administratören.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-1">
                Nuvarande licens
              </div>
              <div className="font-display uppercase tracking-widest text-white">
                {license.used}/{license.license_count} profiler använda
              </div>
            </div>

            {license.pending_request ? (
              <div className="border border-[#FFD60A] bg-[#FFD60A]/10 p-4 text-[#FFD60A] font-display uppercase tracking-widest text-sm"
                data-testid="license-pending-notice">
                Du har redan en väntande förfrågan. Admin är notifierad.
              </div>
            ) : (
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <Checkbox
                  data-testid="license-consent-checkbox"
                  checked={licenseChecked}
                  onCheckedChange={(v) => setLicenseChecked(!!v)}
                  className="mt-1 border-[#3F3F46] data-[state=checked]:bg-[#34C759] data-[state=checked]:text-black rounded-none"
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  Skicka förfrågan om ytterligare licens till administratören.
                </span>
              </label>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setLicenseOpen(false)}
              className="rounded-none border border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
            >
              Avbryt
            </Button>
            {!license.pending_request && (
              <Button
                data-testid="license-request-submit"
                onClick={submitLicenseRequest}
                disabled={!licenseChecked || requestingLicense}
                className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-50"
              >
                {requestingLicense ? "Skickar…" : "Skicka förfrågan"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#111] border-[#27272A] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest">
              Ta bort profil?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              All statistik och alla sessioner för denna målvakt raderas
              permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white">
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-goalkeeper"
              onClick={confirmDelete}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e6342a] text-white font-display uppercase tracking-widest"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
